import React, { useMemo, useState, useEffect } from 'react';
import { StakeSubscribeModal } from './StakeSubscribeModal';
import { MyStaking } from './MyStaking';
import { Card, CardContent } from './ui/card';
import { User, Transaction } from '../App';
import { TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { PaymentModal } from './PaymentModal';
import { NodeDetailsModal } from './NodeDetailsModal';
import { NodeReservationModal } from './NodeReservationModal';
import { MyNodes } from './MyNodes';
import { i18n } from '../utils/i18n';

interface StakingProps {
  user: User;
  onUpdateBalance: (newBalance: number) => void;
  onAddTransaction: (transaction: Transaction) => void;
}

export function Staking({ user, onUpdateBalance, onAddTransaction }: StakingProps) {
  // user가 없으면 로딩 화면 표시
  if (!user) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>{i18n.t('loadingUserInfo')}</p>
        </div>
      </div>
    );
  }

  // 상품·잔여석·신청가능수량은 서버에서 받는다
  const [products, setProducts] = useState<any[]>([]);
  const [seats, setSeats] = useState<{max:number;left:number}|null>(null);
  const [stakeSum, setStakeSum] = useState<{principal:number;earned:number}|null>(null);
  const [lockedHandy, setLockedHandy] = useState(0);
  const [eligible, setEligible] = useState<number|null>(null);
  useEffect(() => {
    (async () => {
      try {
        const j = await fetch('/api/staking/products').then(r => r.json());
        if (j?.success) setProducts(j.products || []);
        const s = await fetch('/api/staking/seats').then(r => r.json());
        if (s?.success) setSeats({ max: s.max, left: s.left });
        const tk = localStorage.getItem('handy-jwt-token') || '';
        if (tk) {
          const lk = await fetch('/api/wallet-lockups', {
            headers: { Authorization: `Bearer ${tk}` } }).then(r => r.json());
          if (lk?.success) setLockedHandy(Number(lk.totalLocked || 0));
        }
        const token = localStorage.getItem('handy-jwt-token');
        if (token) {
          const w = await fetch('/api/staking/welcome/me', {
            headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
          if (w?.success) {
            const live = w.enabled !== false;   // 웰컴 종료 후엔 구독 수익만
            setStakeSum({
              principal: (live ? Number(w.principal || 0) : 0) + Number(w.subs?.principal || 0),
              earned: (live ? Number(w.earned || 0) : 0) + Number(w.subs?.earned || 0),
            });
          }
        }
        const t = localStorage.getItem('handy-jwt-token');
        if (t) {
          const e = await fetch('/api/staking/eligible', {
            headers: { Authorization: `Bearer ${t}` } }).then(r => r.json());
          if (e?.success) setEligible(Math.floor(e.eligible));
        }
      } catch { /* 화면이 이것 때문에 죽으면 안 된다 */ }
    })();
  }, []);

  // 구독 팝업 — 브라우저 confirm/prompt 대신 앱 모달을 쓴다
  const [subProduct, setSubProduct] = useState<any>(null);
  const subscribeTo = (p: any) => setSubProduct(p);

  const doSubscribe = async (amount: number, pin: string) => {
    const token = localStorage.getItem('handy-jwt-token');
    const j = await fetch('/api/staking/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ poolId: subProduct.id, amount, pin }),
    }).then(r => r.json());
    if (!j.success) { alert('❌ ' + (j.error || '신청 실패')); return; }
    setSubProduct(null);
    alert(`구독되었습니다\n${amount.toLocaleString()} wHANDY · ${subProduct.apy}%`);
    location.reload();
  };

  const [activeTab, setActiveTab] = useState<'products' | 'my-staking'>('products');
  const [showPayment, setShowPayment] = useState(false);
  const [showNodeDetails, setShowNodeDetails] = useState(false);
  const [showNodeReservation, setShowNodeReservation] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<{
    amount: number;
    currency: 'USDT' | 'wHANDY';
    title: string;
    description: string;
    type: 'node' | 'premium' | 'basic';
  } | null>(null);

  const apiBase = useMemo(() => {
    const envValue = (import.meta as any)?.env?.VITE_API_URL?.trim?.();
    if (envValue) {
      return envValue.endsWith('/') ? envValue.slice(0, -1) : envValue;
    }
    if (typeof window === 'undefined') return 'https://wallet.handy.app';
    try {
      const url = new URL(window.location.origin);
      if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
        url.hostname = 'localhost';
        url.port = '3600';
        url.protocol = 'http:';
        return url.origin.endsWith('/') ? url.origin.slice(0, -1) : url.origin;
      }
      return url.origin.endsWith('/') ? url.origin.slice(0, -1) : url.origin;
    } catch (error) {
      return 'https://wallet.handy.app';
    }
  }, []);

  const handleComingSoon = () => {
    toast.info('스테이킹 구독은 현재 준비중입니다. 곧 오픈됩니다.');
  };

  const handleSubscribe = (type: 'node' | 'premium' | 'basic') => {
    let config;
    
    switch (type) {
      case 'node':
        config = {
          amount: 120000,
          currency: 'wHANDY' as const,
          title: 'Handy Full Node Subscription',
          description: 'Subscribe to run a validator node - 12만 wHANDY으로 노드 구독 및 보상 받기',
          type: 'node' as const
        };
        break;
      case 'premium':
        config = {
          amount: 5000,
          currency: 'wHANDY' as const,
          title: 'Premium Staking',
          description: 'Stake your wHANDY tokens and earn 25% APY',
          type: 'premium' as const
        };
        break;
      case 'basic':
        config = {
          amount: 3000,
          currency: 'wHANDY' as const,
          title: 'Basic Staking',
          description: 'Start staking with basic plan and earn 18% APY',
          type: 'basic' as const
        };
        break;
    }
    
    setPaymentConfig(config);
    setShowPayment(true);
  };

  const handlePaymentSuccess = async () => {
    if (paymentConfig) {
      try {
        const token = localStorage.getItem('handy-jwt-token');
        if (!token) {
          console.error('인증 토큰이 없습니다.');
          return;
        }

        // 노드 구독인 경우 예약 API 사용
        if (paymentConfig.type === 'node') {
          const response = await fetch(`${apiBase}/api/nodes/reserve`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              productId: 'validator-node',
              amount: paymentConfig.amount,
              pin: prompt(i18n.t('enterPinPrompt'))
            })
          });

          if (!response.ok) {
            throw new Error(i18n.t('nodeReservationFailed'));
          }
          
          const nodeData = await response.json();
          console.log('✅ 노드 예약 완료:', nodeData);
          alert(i18n.t('nodeReservationSuccess').replace('{id}', nodeData.data.reservationId));
          return;
        }

        // 새로운 스테이킹 API 사용
        const response = await fetch(`${apiBase}/api/staking/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            poolId: paymentConfig.type === 'basic' ? 1 : 2, // Basic: 1, Premium: 2
            amount: paymentConfig.amount,
            pin: prompt(i18n.t('enterPinPrompt'))
          })
        });

        if (!response.ok) {
          throw new Error(i18n.t('stakingSubscribeFailed'));
        }

        const result = await response.json();

        // 트랜잭션 추가
        const transaction = {
          type: paymentConfig.title,
          amount: paymentConfig.currency === 'wHANDY' ? -paymentConfig.amount : 0,
          createdAt: new Date().toISOString()
        };
        await onAddTransaction(transaction);

        // wHANDY 잔고 업데이트
        if (paymentConfig.currency === 'wHANDY') {
          await onUpdateBalance(user.balance - paymentConfig.amount);
        }

        console.log('스테이킹 구독 성공:', result);
      } catch (error) {
        console.error('스테이킹 구독 오류:', error);
      }
    }
    setPaymentConfig(null);
  };

  return (
    <div className="h-full bg-background flex flex-col">
      <div className="flex-1 overflow-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <div>
            <h1 className="text-2xl font-light text-foreground">{i18n.t('staking')}</h1>
            <p className="text-muted-foreground">{i18n.t('earnRewards')}</p>
          </div>
          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
            <TrendingUp className="h-5 w-5 text-muted-foreground" />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 border-b border-border">
          <button
            onClick={() => setActiveTab('products')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'products'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {i18n.t('stakingProducts')}
          </button>
          <button
            onClick={() => setActiveTab('my-staking')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'my-staking'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            마이 스테이킹
          </button>
        </div>

        {/* Content */}
        {activeTab === 'my-staking' ? (
          <MyStaking />
        ) : (
          <>

        {/* Balance Card */}
        <Card className="bg-gradient-to-br from-blue-600 to-blue-500 border-0 text-white">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-sm mb-1">총 자산</p>
                <p className="text-3xl font-light">{(Number(user?.balance || 0) + lockedHandy).toFixed(2)}</p>
                <p className="text-white/60 text-sm">wHANDY</p>
              </div>
              {/* 가입을 막아둔 동안에는 수익을 띄우지 않는다. 상품이 하나도
                  없는데 '수익 +4,000' 만 떠 있으면 어디서 난 돈인지 알 수 없다.
                  참여 중인 건이 실제로 있을 때만 보여준다. (2026-10-17) */}
              {stakeSum && stakeSum.principal > 0 && (
                <div className="text-right shrink-0">
                  <p className="text-white/80 text-sm mb-1">스테이킹 수익</p>
                  <p className="text-2xl font-bold" style={{ color: '#BBF7D0' }}>
                    +{stakeSum.earned.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-white/60 text-sm">
                    {(stakeSum.earned / stakeSum.principal * 100).toFixed(2)}%
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>




        {/* 상품 목록 — 서버가 주는 것을 그린다 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* 가입을 막아둔 동안에도 어떤 상품이 올지는 보여준다.
              빈 화면만 띄우면 서비스가 없는 줄 안다. (2026-10-17) */}
          {products.length === 0 && [
            { name: '로얄 스테이킹', months: 6, apy: 16 },
            { name: '로얄 스테이킹', months: 12, apy: 40 },
          ].map((p, i) => (
            <Card key={i} style={{ opacity: .75 }}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-bold text-lg">{p.name}</p>
                    <p className="text-gray-500 text-sm mt-0.5">{p.months}개월 · 연 {p.apy}%</p>
                  </div>
                  <span style={{
                    background: '#F3F4F6', color: '#6B7280', fontSize: 12, fontWeight: 700,
                    padding: '6px 12px', borderRadius: 999, whiteSpace: 'nowrap',
                  }}>준비중</span>
                </div>
                <button disabled style={{
                  width: '100%', marginTop: 14, padding: 13, border: 0, borderRadius: 12,
                  background: '#E5E7EB', color: '#9CA3AF', fontWeight: 700, fontSize: 15,
                }}>곧 시작합니다</button>
              </CardContent>
            </Card>
          ))}
          {products.map((p: any) => {
            const welcome = /웰컴/.test(p.name);
            const royal = /로얄/.test(p.name);
            const months = Math.round(p.lockPeriodDays / 30);
            const expect = Math.round(p.minAmount * p.apy / 100);
            const full = !welcome && seats !== null && seats.left <= 0;
            const bg = welcome ? '#F97316' : royal ? (months >= 12 ? '#7C3AED' : '#0D9488') : '#2563EB';
            return (
              <div key={p.id} className="rounded-3xl p-6 text-white" style={{ background: bg }}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <span className="inline-block rounded-full px-3 py-1 text-xs font-bold"
                          style={{ background: 'rgba(0,0,0,.22)' }}>
                      {welcome ? '구독중' : royal ? 'HANDY ROYAL' : seats ? `선착순 ${seats.max.toLocaleString()}명` : '선착순'}
                    </span>
                    <h3 className="text-2xl font-bold mt-2">{p.name}</h3>
                  </div>
                  <div className="text-right shrink-0 pl-3">
                    <p className="text-4xl font-bold leading-none">{p.apy}%</p>
                    <p className="text-sm mt-1" style={{ opacity: .9 }}>{months}개월</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-5">
                  {[['최소 수량', p.minAmount.toLocaleString()],
                    ['기간', months + '개월'],
                    ['예상 수익', '+' + expect.toLocaleString()]].map(([k, v]) => (
                    <div key={k} className="rounded-2xl py-3 text-center"
                         style={{ background: 'rgba(0,0,0,.22)' }}>
                      <p className="text-xs" style={{ opacity: .85 }}>{k}</p>
                      <p className="font-bold mt-1">{v}</p>
                    </div>
                  ))}
                </div>

                {welcome ? (
                  <div className="mt-5 rounded-2xl py-4 text-center font-bold"
                       style={{ background: 'rgba(0,0,0,.22)' }}>
                    자동 구독중 · 마이 스테이킹에서 확인
                  </div>
                ) : royal ? (
                  <button onClick={() => subscribeTo(p)}
                    className="mt-5 w-full rounded-2xl py-4 font-bold text-lg"
                    style={{ background: 'rgba(0,0,0,.3)', color: '#FFFFFF' }}
                  >
                    구독하기
                  </button>
                ) : (
                  <button disabled
                    className="mt-5 w-full rounded-2xl py-4 font-bold text-lg"
                    style={{ background: 'rgba(0,0,0,.25)', color: 'rgba(255,255,255,.85)' }}
                  >
                    오픈 예정
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <StakeSubscribeModal
          open={!!subProduct}
          onClose={() => setSubProduct(null)}
          product={subProduct}
          eligible={eligible ?? 0}
          onConfirm={doSubscribe}
          onGoBuy={() => { try { window.dispatchEvent(new CustomEvent('handy:go-buy')); } catch {} }}
        />
        </>
        )}
      </div>

      {/* Payment Modal */}
      {paymentConfig && (
        <PaymentModal
          isOpen={showPayment}
          onClose={() => setShowPayment(false)}
          amount={paymentConfig.amount}
          currency={paymentConfig.currency}
          title={paymentConfig.title}
          description={paymentConfig.description}
          onSuccess={handlePaymentSuccess}
          userBalance={user.balance}
        />
      )}

      {/* Node Details Modal */}
      <NodeDetailsModal
        isOpen={showNodeDetails}
        onClose={() => setShowNodeDetails(false)}
        nodeId="validator-node-001"
      />

      {/* Node Reservation Modal */}
      <NodeReservationModal
        isOpen={showNodeReservation}
        onClose={() => setShowNodeReservation(false)}
        onPayment={() => {
          setShowNodeReservation(false);
          handleSubscribe('node');
        }}
        user={user}
      />
    </div>
  );
}